import apiClient from "./apiClient";
import { AxiosError } from "axios";
import { captureFatalException } from "@/config/sentry";

/**
 * Interface for registration OTP request
 */
interface RegistrationOTPRequestData {
  userType: "individual" | "company";
  firstName?: string;
  lastName?: string;
  companyName?: string;
  siret?: string;
  email: string;
  password: string;
  phone?: string;
  address?: string;
  city?: string;
  postalCode?: string;
}

/**
 * Interface for OTP verification with userId
 */
interface OTPVerificationWithUserIdData {
  userId: string;
  otpCode: string;
}

/**
 * Interface for credentials
 */
interface CredentialsData {
  email: string;
  password: string;
}

/**
 * Interface for OTP verification
 */
interface OTPVerificationData {
  email: string;
  otp: string;
}

/**
 * Interface for password reset data
 */
interface PasswordResetData {
  email: string;
  password: string;
}

interface PasswordChangeData {
  otpCode: string;
  newPassword: string;
}

/**
 * User data interface
 */
interface UserData {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string | null;
  address: string | null;
  city: string | null;
  postalCode: string | null;
  userType: "individual" | "company";
  companyName: string | null;
  siret: string | null;
  isEmailVerified: boolean;
  subscriptionPlan: string | null;
  subscriptionStatus: string | null;
  subscriptionEndDate: string | null;
  enabledFeatures: string[];
  createdAt: string;
  updatedAt: string;
}

/**
 * Authentication status response interface
 */
interface AuthStatusResponse {
  success: boolean;
  isAuthenticated: boolean;
  message?: string;
  redirectTo?: string;
  user?: UserData;
  tokenExpiringWarning: boolean;
  // Individual user fields are available at the root level for backward compatibility
  id?: string;
  email?: string;
  firstName?: string | null;
  lastName?: string | null;
  phone?: string | null;
  address?: string | null;
  city?: string | null;
  postalCode?: string | null;
  userType?: "individual" | "company";
  companyName?: string | null;
  siret?: string | null;
  isEmailVerified?: boolean;
  subscriptionPlan?: string | null;
  subscriptionStatus?: string | null;
  subscriptionEndDate?: string | null;
  enabledFeatures?: string[];
  createdAt?: string;
  updatedAt?: string;
}

interface StripeSessionVerificationData {
  sessionId: string;
}

interface StripeSessionVerificationResponse {
  success: boolean;
  message: string;
  user: {
    id: string;
    email: string;
    firstName: string;
    lastName: string;
    subscriptionPlan: string;
    subscriptionStatus: string;
    subscriptionEndDate: string;
    customerId: string;
    subscriptionId: string;
  };
}

/**
 * Authentication service for handling all auth-related API calls
 */
const authService = {
  /**
   * Request OTP for registration
   */
  requestRegistrationOTP: async (
    data: RegistrationOTPRequestData,
  ): Promise<{ message: string; userId: string; otp?: string }> => {
    try {
      const response = await apiClient.post(
        "/api/auth/register/request-otp",
        data,
      );
      return response.data;
    } catch (error: unknown) {
      if (error instanceof AxiosError && error.response) {
        throw new Error(
          error.response.data.message || "Failed to request registration OTP",
        );
      }
      throw new Error("An unexpected error occurred");
    }
  },

  /**
   * Verify OTP and complete registration
   */
  verifyRegistrationOTP: async (
    data: OTPVerificationWithUserIdData,
  ): Promise<{ message: string; user: UserData }> => {
    try {
      const response = await apiClient.post(
        "/api/auth/register/verify-otp",
        data,
      );
      return response.data;
    } catch (error: unknown) {
      if (error instanceof AxiosError && error.response) {
        throw new Error(
          error.response.data.message || "Failed to verify registration OTP",
        );
      }
      throw new Error("An unexpected error occurred");
    }
  },

  /**
   * Verify credentials and request OTP for login
   * This is a two-factor authentication first step
   */
  verifyCredentialsAndRequestOTP: async (
    data: CredentialsData,
  ): Promise<{
    success: boolean;
    message: string;
    userId: string;
    otp?: string;
    requiresEmailVerification?: boolean;
  }> => {
    try {
      const response = await apiClient.post(
        "/api/auth/login/verify-credentials",
        data,
      );
      return response.data;
    } catch (error: unknown) {
      if (error instanceof AxiosError && error.response) {
        throw new Error(
          error.response.data.message || "Invalid email or password",
        );
      }
      throw new Error("An unexpected error occurred");
    }
  },

  /**
   * Verify OTP and complete login with userId and otpCode
   */
  verifyLoginOTPAndSignIn: async (
    data: OTPVerificationWithUserIdData,
  ): Promise<{ message: string; user: UserData }> => {
    try {
      const response = await apiClient.post("/api/auth/login/verify-otp", data);
      return response.data;
    } catch (error: unknown) {
      if (error instanceof AxiosError && error.response) {
        throw new Error(
          error.response.data.message || "Failed to verify login OTP",
        );
      }
      throw new Error("An unexpected error occurred");
    }
  },

  /**
   * Verify OTP and complete login with email and otp
   */
  verifyLoginOTP: async (
    data: OTPVerificationData,
  ): Promise<{ token: string; user: UserData }> => {
    try {
      const response = await apiClient.post("/api/auth/login/verify-otp", data);
      return response.data;
    } catch (error: unknown) {
      if (error instanceof AxiosError && error.response) {
        throw new Error(
          error.response.data.message || "Failed to verify login OTP",
        );
      }
      throw new Error("An unexpected error occurred");
    }
  },

  /**
   * Legacy method: Request OTP for login
   * This is kept for backward compatibility
   * @deprecated Use verifyCredentialsAndRequestOTP instead
   */
  requestLoginOTP: async (
    email: string,
  ): Promise<{ success: boolean; message: string }> => {
    try {
      const response = await apiClient.post("/api/auth/login/request-otp", {
        email,
      });
      return response.data;
    } catch (error: unknown) {
      if (error instanceof AxiosError && error.response) {
        throw new Error(
          error.response.data.message || "Failed to request login OTP",
        );
      }
      throw new Error("An unexpected error occurred");
    }
  },

  /**
   * Request OTP for password reset
   */
  requestPasswordResetOTP: async (
    email: string,
  ): Promise<{ success: boolean; message: string }> => {
    try {
      const response = await apiClient.post(
        "/api/auth/forgot-password/request-otp",
        {
          email,
        },
      );
      return response.data;
    } catch (error: unknown) {
      if (error instanceof AxiosError && error.response) {
        throw new Error(
          error.response.data.message || "Failed to request password reset OTP",
        );
      }
      throw new Error("An unexpected error occurred");
    }
  },

  /**
   * Verify password reset OTP
   */
  verifyPasswordResetOTP: async (
    data: OTPVerificationData,
  ): Promise<{ success: boolean; message: string }> => {
    try {
      const response = await apiClient.post(
        "/api/auth/forgot-password/verify-otp",
        data,
      );
      return response.data;
    } catch (error: unknown) {
      if (error instanceof AxiosError && error.response) {
        throw new Error(
          error.response.data.message || "Failed to verify password reset OTP",
        );
      }
      throw new Error("An unexpected error occurred");
    }
  },

  /**
   * Reset password after OTP verification
   */
  resetPassword: async (
    data: PasswordResetData,
  ): Promise<{ success: boolean; message: string }> => {
    try {
      const response = await apiClient.post("/api/auth/reset-password", data);
      return response.data;
    } catch (error: unknown) {
      if (error instanceof AxiosError && error.response) {
        throw new Error(
          error.response.data.message || "Failed to reset password",
        );
      }
      throw new Error("An unexpected error occurred");
    }
  },

  /**
   * Logout user
   */
  logout: async (): Promise<void> => {
    try {
      await apiClient.post("/api/auth/logout");
    } catch (error) {
      console.error("Logout error:", error);
    }
  },

  /**
   * Get current user profile
   */
  getCurrentUser: async (): Promise<UserData> => {
    try {
      const response = await apiClient.get("/api/auth/me");
      return response.data;
    } catch (error: unknown) {
      if (error instanceof AxiosError && error.response) {
        throw new Error(
          error.response.data.message || "Failed to get user profile",
        );
      }
      throw new Error("An unexpected error occurred");
    }
  },

  /**
   * Update user profile
   */
  updateProfile: async (profileData: {
    firstName?: string;
    lastName?: string;
    phone?: string;
    address?: string;
    city?: string;
    postalCode?: string;
    userType?: "individual" | "company";
    companyName?: string;
    siret?: string;
  }): Promise<{ message: string; user: Partial<UserData> }> => {
    try {
      const response = await apiClient.put("/api/users/profile", profileData);
      return response.data;
    } catch (error: unknown) {
      if (error instanceof AxiosError && error.response) {
        throw new Error(
          error.response.data.message || "Failed to update profile",
        );
      }
      throw new Error("An unexpected error occurred");
    }
  },

  /**
   * Check authentication status with the backend
   * Uses HTTP-only cookies for authentication
   */
  checkStatus: async (): Promise<AuthStatusResponse> => {
    try {
      const response = await apiClient.get("/api/status");
      const data = response.data as AuthStatusResponse;

      // Check for token expiration warning header
      const tokenExpiringWarning =
        response.headers["x-token-expiring-soon"] === "true";

      if (!data.success || !data.isAuthenticated) {
        return {
          success: false,
          isAuthenticated: false,
          message: data.message || "Not authenticated",
          redirectTo: data.redirectTo || "/login",
          tokenExpiringWarning: false,
        };
      }

      // Convert API response to user data format
      const userData: UserData = {
        id: data.id!,
        email: data.email!,
        firstName: data.firstName || "",
        lastName: data.lastName || "",
        phone: data.phone || null,
        address: data.address || null,
        city: data.city || null,
        postalCode: data.postalCode || null,
        userType: data.userType || "individual",
        companyName: data.companyName || null,
        siret: data.siret || null,
        isEmailVerified: data.isEmailVerified || false,
        subscriptionPlan: data.subscriptionPlan || null,
        subscriptionStatus: data.subscriptionStatus || null,
        subscriptionEndDate: data.subscriptionEndDate || null,
        enabledFeatures: data.enabledFeatures || [],
        createdAt: data.createdAt || new Date().toISOString(),
        updatedAt: data.updatedAt || new Date().toISOString(),
      };

      return {
        success: true,
        isAuthenticated: true,
        user: userData,
        tokenExpiringWarning,
      } as AuthStatusResponse;
    } catch (error) {
      if (error instanceof AxiosError && error.response?.data) {
        const errorData = error.response.data as AuthStatusResponse;
        return {
          success: false,
          isAuthenticated: false,
          message: errorData.message || "Authentication check failed",
          redirectTo: errorData.redirectTo || "/login",
          tokenExpiringWarning: false,
        };
      }

      return {
        success: false,
        isAuthenticated: false,
        message: "Failed to check authentication status",
        redirectTo: "/login",
        tokenExpiringWarning: false,
      };
    }
  },

  /**
   * Verify Stripe session and update user subscription
   */
  verifyStripeSession: async (
    data: StripeSessionVerificationData,
  ): Promise<StripeSessionVerificationResponse> => {
    try {
      const response = await apiClient.post(
        "/api/users/verify-stripe-session",
        data,
      );
      return response.data;
    } catch (error: unknown) {
      // ✅ ERREUR FATALE : Vérification de session Stripe échouée après paiement
      const fatalError =
        error instanceof Error
          ? error
          : new Error("Failed to verify Stripe session");

      captureFatalException(fatalError, {
        operation: "verify_stripe_session",
        sessionId: data.sessionId,
        requires_immediate_attention: true,
        impact: "payment_verification_failed",
        financial_impact: true,
      });

      if (error instanceof AxiosError && error.response) {
        throw new Error(
          error.response.data.message || "Failed to verify Stripe session",
        );
      }
      throw new Error(
        "An unexpected error occurred during Stripe verification",
      );
    }
  },

  /**
   * Request OTP for email change
   */
  requestEmailChangeOTP: async (
    newEmail: string,
  ): Promise<{ success: boolean; message: string; otp?: string }> => {
    try {
      const response = await apiClient.post(
        "/api/auth/change-email/request-otp",
        {
          newEmail,
        },
      );
      return response.data;
    } catch (error: unknown) {
      if (error instanceof AxiosError && error.response) {
        throw new Error(
          error.response.data.message || "Failed to request email change OTP",
        );
      }
      throw new Error("An unexpected error occurred");
    }
  },

  /**
   * Verify email change OTP
   */
  verifyEmailChangeOTP: async (
    otpCode: string,
  ): Promise<{ success: boolean; message: string; user: UserData }> => {
    try {
      const response = await apiClient.post(
        "/api/auth/change-email/verify-otp",
        {
          otpCode,
        },
      );
      return response.data;
    } catch (error: unknown) {
      if (error instanceof AxiosError && error.response) {
        throw new Error(
          error.response.data.message || "Failed to verify email change OTP",
        );
      }
      throw new Error("An unexpected error occurred");
    }
  },

  /**
   * Request OTP for password change
   */
  requestPasswordChangeOTP: async (): Promise<{
    success: boolean;
    message: string;
    otp?: string;
  }> => {
    try {
      const response = await apiClient.post(
        "/api/auth/change-password/request-otp",
      );
      return response.data;
    } catch (error: unknown) {
      if (error instanceof AxiosError && error.response) {
        throw new Error(
          error.response.data.message ||
            "Failed to request password change OTP",
        );
      }
      throw new Error("An unexpected error occurred");
    }
  },

  /**
   * Verify password change OTP and update password
   */
  verifyPasswordChangeOTP: async (
    data: PasswordChangeData,
  ): Promise<{ success: boolean; message: string; user: UserData }> => {
    try {
      const response = await apiClient.post(
        "/api/auth/change-password/verify-otp",
        data,
      );
      return response.data;
    } catch (error: unknown) {
      if (error instanceof AxiosError && error.response) {
        throw new Error(
          error.response.data.message || "Failed to verify password change OTP",
        );
      }
      throw new Error("An unexpected error occurred");
    }
  },
};

export default authService;
